export type TParsingStatus =
	| "pending"
	| "submitted"
	| "completed"
	| "failed"
	| "skipped"
	| "processing"

export type TParsingClassification =
	| "quotation_request"
	| "support"
	| "not_relevant"
	| "no_enquiry_found"
	| "spam"
	| string

export type TParsingExtraction = {
	guest_name?: string | null
	guest_salutation?: string | null
	destinations: string[]
	start_date?: string | null
	no_of_nights?: number | null
	no_of_adults?: number | null
	no_of_children?: number | null
	ages_of_children?: number[]
	contact_email?: string | null
	contact_name?: string | null
	comments?: string | null
	contact_person?: string | null
	contact_phone?: string | null
	guest_phone_numbers?: string | string[] | null
	budget?: number | string | null
	budget_currency?: string | null
	meal_preferences?: string | null
	accomodation_category?: string | null
}

export type TParsingFeedbackCategory =
	| "wrong_category_given"
	| "wrong_extraction_given"
	| "others"

export type TParsingSnapshot = {
	id: number
	status: TParsingStatus
	skip_reason?: string | null
	classification?: TParsingClassification | null
	classification_label?: string | null
	error?: string | null
	submitted_at?: string | null
	completed_at?: string | null
	extraction?: TParsingExtraction | null
	feedback_status?: 0 | 1 | null
	feedback_category?: TParsingFeedbackCategory | string | null
	feedback_remarks?: string | null
	credits?: number | null
	created_at: string
}

export const PARSING_FEEDBACK_CATEGORIES: Array<{
	value: TParsingFeedbackCategory
	label: string
	type: 0 | 1
}> = [
	{ value: "wrong_category_given", label: "Wrong category", type: 0 },
	{ value: "wrong_extraction_given", label: "Wrong extraction", type: 0 },
	{ value: "others", label: "Other", type: 0 },
]

export const PARSING_SKIP_REASON_LABELS: Record<string, string> = {
	already_linked: "Thread already linked to an entity",
	thread_has_history: "Reply in an existing thread",
	parser_disabled: "Parser disabled",
	credit_exhausted: "Tenant credit exhausted",
	provider_key_missing: "AI parsing not enabled",
	empty_content: "Awaiting message content",
	stale_backlog: "Older backlog message — not auto-parsed",
}

export type TParsingBadge = {
	label: string
	color: "muted" | "success" | "warning" | "danger"
	detail?: string
}

export function getParsingBadge(
	parsing: TParsingSnapshot | null | undefined
): TParsingBadge | null {
	if (!parsing) return null
	switch (parsing.status) {
		case "pending":
			return { label: "Parsing queued", color: "muted" }
		case "submitted":
			return { label: "Parsing in progress", color: "muted" }
		case "completed": {
			const label = parsing.classification_label
				? `${parsing.classification_label}`
				: "Parsed"
			const color: "success" | "muted" =
				parsing.classification?.toLowerCase() === "quotation_request"
					? "success"
					: "muted"
			return { label, color }
		}
		case "failed":
			return {
				label: "Parsing failed",
				color: "danger",
				detail: parsing.error || undefined,
			}
		case "skipped": {
			const reason = parsing.skip_reason || ""
			return {
				label: "Parsing skipped",
				color: "warning",
				detail: PARSING_SKIP_REASON_LABELS[reason] || reason || undefined,
			}
		}
		case "processing":
			return {
				label: "Parsing processing",
				color: "muted",
			}
		default:
			return null
	}
}
